/**
 * A plant skid's dialog - the facilities technician's half of the loop,
 * moved out of main.ts and into an antd Modal.
 *
 * The repair is real in both directions: performing it on the sick plant
 * clears the house event for the whole building, and performing it on a
 * healthy one charges the same hours and changes nothing - the discipline
 * every tool upstairs already enforces. Readings and the repair verdict are
 * frozen at open, as before: the question this dialog answers is "what did
 * you find when you walked up", not a live feed.
 */

import { Modal } from 'antd'
import { lazy, Suspense, useMemo, useState } from 'react'
import {
  repairOutcome,
  skidReadings,
  type HouseBus,
  type HouseSystem,
} from '../../sim/house/model'
import type { Journal } from '../../learn/Journal'
import { t, tOr } from '../../i18n/i18n'
import { useLang } from '../i18n/LanguageProvider'

const DOT = { good: '#4fae6d', warning: '#d8a020', critical: '#e0564a' } as const

// The pump and water skids carry their full instrument sets - lazy, because
// the charts are a chunk of their own and three of the five skids never need
// them.
const VacuumPlantView = lazy(() =>
  import('../../tools/plantViews').then((m) => ({ default: m.VacuumPlantView })),
)
const UpwPlantView = lazy(() =>
  import('../../tools/plantViews').then((m) => ({ default: m.UpwPlantView })),
)

export function SkidDialog({
  system,
  journal,
  house,
  onClose,
}: {
  system: HouseSystem
  journal: Journal
  house: HouseBus
  onClose: () => void
}) {
  useLang()
  const readings = useMemo(() => skidReadings(system, house.state), [system, house])
  const outcome = useMemo(() => repairOutcome(system, house.armed), [system, house])
  const [verdict, setVerdict] = useState<{ message: string; cleared: boolean } | null>(null)

  const hasInstruments = system === 'vacuum' || system === 'upw'

  const repair = (): void => {
    journal.record({
      kind: 'maintenance',
      action: `house:${system}`,
      label: t(`house.system.${system}.Label`),
      hours: outcome.hours,
      tool: 'FAC-06',
    })
    const hadEvent = house.armed !== null
    if (outcome.clears) house.arm(null)
    const message = outcome.clears
      ? tOr(
          'subfab.RepairCleared',
          'Repair complete and the loop re-qualified. The utility board is green again — ' +
            'walk upstairs and watch the tools recover.',
        )
      : hadEvent
        ? tOr(
            'subfab.RepairWasted',
            'Repaired, re-certified… and the symptom upstairs has not moved. This plant ' +
              'was healthy; the hours are spent.',
          )
        : tOr(
            'subfab.RepairIdle',
            'Nothing was wrong with this plant. Preventive work, logged and charged.',
          )
    setVerdict({ message, cleared: outcome.clears })
  }

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={t(`house.system.${system}.Label`)}
      width={hasInstruments ? 'min(740px, 96vw)' : 420}
      styles={hasInstruments ? { body: { maxHeight: '76vh', overflowY: 'auto' } } : undefined}
    >
      <div className="modal-body">
        <div style={{ marginBottom: 12 }}>
          {tOr(
            'subfab.SkidNote',
            'The building’s side of the story. Repairs performed here are real: the hours are ' +
              'charged either way, and only the plant that is actually sick recovers anything.',
          )}
        </div>
        {readings.map((r) => (
          <div
            key={r.id}
            style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '6px 0' }}
          >
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                flex: 'none',
                background: DOT[r.state],
              }}
            />
            <span style={{ flex: 1 }}>{tOr(`subfab.reading.${r.id}`, r.label)}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.value}</span>
          </div>
        ))}
      </div>
      {hasInstruments && (
        <Suspense fallback={null}>
          {system === 'vacuum' ? <VacuumPlantView /> : <UpwPlantView />}
        </Suspense>
      )}
      <div className="modal-actions">
        {verdict ? (
          <div
            style={{
              flex: 1,
              fontSize: '12.5px',
              lineHeight: 1.55,
              color: verdict.cleared ? DOT.good : DOT.warning,
            }}
          >
            {verdict.message}
          </div>
        ) : (
          <button
            className="btn primary"
            onClick={repair}
          >{`${t(`house.system.${system}.Action`).replace(/^[^:：]*[:：]\s*/, '')} — ${outcome.hours} h`}</button>
        )}
        <button className="btn" onClick={onClose}>
          {t('panel.Close')}
        </button>
      </div>
    </Modal>
  )
}
